import { createContext, useContext, useMemo, type ReactNode } from 'react';
import { useQuery, type QueryClient } from '@tanstack/react-query';
import { useSelector } from 'react-redux';
import { getLikedUsers } from '@/api/user';
import { RootState } from '@/store';
import {
    likedIdSet,
    likedUsersQueryKey,
    withLikedUser,
    type LikedUsersResponse,
} from './likedUsersCache';

export { likedIdSet, likedUsersQueryKey, withLikedUser };
export type { LikedUserRecord, LikedUsersResponse } from './likedUsersCache';

const LikedUsersContext = createContext<Set<string>>(likedIdSet(undefined));

export async function applyLikedUser(queryClient: QueryClient, likedUserId: string, liked: boolean) {
    await queryClient.cancelQueries({ queryKey: likedUsersQueryKey });
    let hadList = false;
    queryClient.setQueryData<LikedUsersResponse | undefined>(likedUsersQueryKey, (current) => {
        hadList = Boolean(current);
        return withLikedUser(current, likedUserId, liked);
    });
    // A list that had not arrived yet would be only this one id. Reload the full set.
    if (!hadList) {
        await queryClient.invalidateQueries({ queryKey: likedUsersQueryKey });
    }
}

export const likedUsersQueryOptions = {
    queryKey: likedUsersQueryKey,
    queryFn: async (): Promise<LikedUsersResponse> => getLikedUsers(),
    staleTime: 60_000,
};

export function LikedUsersProvider({ children }: { children: ReactNode }) {
    const userId = useSelector((state: RootState) => state.auth.user?._id);
    const query = useQuery({
        ...likedUsersQueryOptions,
        enabled: Boolean(userId),
    });
    const ids = useMemo(
        () => (userId ? likedIdSet(query.data) : likedIdSet(undefined)),
        [query.data, userId],
    );

    return <LikedUsersContext.Provider value={ids}>{children}</LikedUsersContext.Provider>;
}

export function useLikedUserSet(): Set<string> {
    return useContext(LikedUsersContext);
}
